import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, IndianRupee, Loader2, Scale, XCircle } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import { PENALTY_APPEAL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input } from "../../components/ui/input";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";

const STATUS_TABS = [
    { value: "pending", label: "Pending" },
    { value: "waived", label: "Waived" },
    { value: "reduced", label: "Reduced" },
    { value: "rejected", label: "Rejected" },
    { value: "all", label: "All" },
];

const STATUS_CHIP = {
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    waived: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    reduced: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    rejected: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
};

const PenaltyAppeals = () => {
    const { fetch, update } = useApiMethods();
    const fetchRef = useRef(fetch);
    const updateRef = useRef(update);
    fetchRef.current = fetch;
    updateRef.current = update;

    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
    const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const { permission: viewPermission, edit: editPermission } = checkUserPermissionFunc(
        "attendance",
        "Penalty Appeals",
        "SubMenu",
    );
    const canView = viewPermission || isAdmin || isSuperAdmin;
    const canReview = editPermission || isAdmin || isSuperAdmin;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [reviewOpen, setReviewOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [action, setAction] = useState("waive");
    const [adminComment, setAdminComment] = useState("");
    const [reducedFine, setReducedFine] = useState("");
    const [reducedDeduction, setReducedDeduction] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!canView) return undefined;

        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                const res = await fetchRef.current(PENALTY_APPEAL, {
                    status: statusFilter === "all" ? undefined : statusFilter,
                });
                if (cancelled) return;
                if (res?.status === "success") {
                    const data = res.data?.result || res.data || [];
                    setRows(Array.isArray(data) ? data : []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load appeals");
                }
            } catch (err) {
                if (!cancelled) ToastMsg("error", err?.message || "Failed to load appeals");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canView, statusFilter]);

    const reloadAppeals = async () => {
        const res = await fetchRef.current(PENALTY_APPEAL, {
            status: statusFilter === "all" ? undefined : statusFilter,
        });
        if (res?.status === "success") {
            const data = res.data?.result || res.data || [];
            setRows(Array.isArray(data) ? data : []);
        }
    };

    const openReview = (row, nextAction) => {
        setSelected(row);
        setAction(nextAction);
        setAdminComment("");
        setReducedFine(String(row.fine_amount || 0));
        setReducedDeduction(String(row.deduction_days || 0));
        setReviewOpen(true);
    };

    const submitReview = async () => {
        if (!selected?.id) return;
        setSaving(true);
        try {
            const payload = {
                id: selected.id,
                action,
                admin_comment: adminComment.trim(),
            };
            if (action === "reduce") {
                if (selected.penalty_mode === "fine") {
                    payload.reduced_fine = Number(reducedFine) || 0;
                } else {
                    payload.reduced_deduction = Number(reducedDeduction) || 0;
                }
            }
            const res = await updateRef.current(PENALTY_APPEAL, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Appeal updated");
                setReviewOpen(false);
                await reloadAppeals();
            } else {
                ToastMsg("error", res?.message || "Failed to update appeal");
            }
        } catch (err) {
            ToastMsg("error", err?.message || "Failed to update appeal");
        } finally {
            setSaving(false);
        }
    };

    if (!canView) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        You do not have permission to view penalty appeals. Ask your administrator to enable
                        Attendance → Penalty Appeals in User Permissions.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 p-3 rounded-lg">
                <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <Scale className="w-6 h-6 text-sidebar-primary flex-shrink-0 mt-0.5 sm:mt-0" />
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Penalty Appeals
                            <span className="text-muted-foreground font-normal ml-2">
                                ({rows.length})
                            </span>
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Review and resolve employee concerns before payroll is finalized
                        </p>
                    </div>
                </div>

                <SegmentSwitch
                    options={STATUS_TABS}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    className="self-start lg:self-center"
                />
            </div>

            <div className="px-3 pb-6 space-y-3">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="w-7 h-7 animate-spin text-sidebar-primary" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-10 text-center text-sm text-muted-foreground">
                        No {statusFilter === "all" ? "" : statusFilter} penalty appeals.
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {rows.map((row) => (
                            <div
                                key={row.id}
                                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                            >
                                <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {row.employee_name}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {row.employee_code}
                                                </div>
                                            </div>
                                            <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", STATUS_CHIP[row.status])}>
                                                {row.status_label}
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {row.date_display} · {row.appeal_type_label}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{row.reason}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            Penalty: {row.penalty_mode === "fine"
                                                ? `₹${Number(row.fine_amount || 0).toLocaleString("en-IN")}`
                                                : `${row.deduction_days} day LOP`}
                                            {row.requested_on ? ` · Submitted ${row.requested_on}` : ""}
                                        </p>
                                        {row.admin_comment && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                HR note: {row.admin_comment}
                                            </p>
                                        )}
                                    </div>
                                    {row.status === "pending" && canReview && (
                                        <div className="flex flex-wrap gap-2 shrink-0">
                                            <Button size="sm" variant="outline" onClick={() => openReview(row, "waive")}>
                                                <CheckCircle2 className="w-4 h-4 mr-1" /> Waive
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => openReview(row, "reduce")}>
                                                <IndianRupee className="w-4 h-4 mr-1" /> Reduce
                                            </Button>
                                            <Button size="sm" variant="outline" className="text-rose-600" onClick={() => openReview(row, "reject")}>
                                                <XCircle className="w-4 h-4 mr-1" /> Reject
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="rounded-xl border border-amber-200/70 bg-amber-50/80 dark:bg-amber-950/20 dark:border-amber-900/40 px-4 py-3 text-xs text-amber-900 dark:text-amber-200 flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p>
                        Resolve appeals before running payroll. <strong>Waive</strong> removes the penalty;
                        <strong> Reduce</strong> adjusts the fine or LOP amount; <strong>Reject</strong> keeps the penalty and allows the employee to re-appeal.
                    </p>
                </div>
            </div>

            <DialogModal
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onSave={submitReview}
                title={action === "waive" ? "Waive penalty" : action === "reduce" ? "Reduce penalty" : "Reject appeal"}
                closeLabel="Cancel"
                saveLabel="Confirm"
                loading={saving}
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        {selected?.employee_name} · {selected?.date_display} · {selected?.appeal_type_label}
                    </p>
                    {action === "reduce" && (
                        selected?.penalty_mode === "fine" ? (
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reduced fine (₹)</label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={reducedFine}
                                    onChange={(e) => setReducedFine(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                        ) : (
                            <div>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reduced LOP (days)</label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.25"
                                    value={reducedDeduction}
                                    onChange={(e) => setReducedDeduction(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                        )
                    )}
                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Comment for employee</label>
                        <textarea
                            className="mt-1 w-full min-h-[80px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            value={adminComment}
                            onChange={(e) => setAdminComment(e.target.value)}
                            placeholder="Optional note visible in the employee's penalty history"
                        />
                    </div>
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default PenaltyAppeals;
